/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import Input from "components/Input";
import Button from "components/Button";
import reimbursementManager from "services/Reimbursement/reimbursementManager";
import adminConfigService from "services/AdminConfig/adminConfigService";
import { ReimbursementResponse, ReimbursementFile } from "services/Reimbursement/reimbursementInterface";
import { dateFormate } from "util/Util";
import formatCurrency from "util/currency";
import { getImageUrl } from "util/googleDriveHelper";
import { useAppContext } from "contexts/useAppContext";

const MAX_FILE_SIZE = 2 * 1024 * 1024;
// MIME types accepted for reimbursement receipt uploads (images + PDF)
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/jfif", "application/pdf"];
// `accept` attribute value for the receipt file <input>
const FILE_ACCEPT = "image/jpeg,image/jpg,image/png,image/jfif,.jfif,application/pdf";
// Validation error shown when the chosen file's type is not allowed
const INVALID_FILE_TYPE_MESSAGE = "Only JPG, PNG, JFIF, or PDF allowed";
// Helper text describing the accepted formats and size limit
const FILE_HELP_TEXT = "JPG, PNG, JFIF, PDF · Max 2 MB";
// Max attachments per reimbursement (existing kept + newly added)
const MAX_FILES = 3;

const uid = () => Math.random().toString(36).slice(2, 10);

// Best-effort attachment type detection from a filename or URL (tolerates the
// presigned query string). Falls back to "image" for extension-less Drive URLs.
const isPdfLike = (value?: string | null) => !!value && /\.pdf(\?|$)/i.test(value);

// Build the existing-attachment list from whichever shape the API returns:
//  • new shape → `files: [{ id, url, originalName }]` (real ids for `fileIds`)
//  • legacy shape → `data: string | string[]` (URLs only; no ids → synthetic
//    negative ids so they still render, and are excluded from `fileIds`)
const buildExistingFiles = (row?: ReimbursementResponse): ReimbursementFile[] => {
  if (!row) return [];
  if (Array.isArray(row.files) && row.files.length) return row.files;

  const urls = Array.isArray(row.data) ? row.data : row.data ? [row.data] : [];
  return urls
    .filter((url): url is string => typeof url === "string" && !!url.trim())
    .map((url, index) => ({
      id: -(index + 1),
      url,
      originalName: row.dataOriginalName ?? null,
    }));
};

// A newly added (not-yet-persisted) attachment with its own upload state.
interface NewAttachment {
  id: string;
  file: File;
  previewUrl: string | null;
  s3Key: string | null;
  uploading: boolean;
  uploadError: string;
}

// Static fallback used while the API call is in-flight
const FALLBACK_TYPES = [
  { value: "Travel", label: "Travel" },
  { value: "Office", label: "Office" },
  { value: "Hardware", label: "Hardware" },
  { value: "Software", label: "Software" },
  { value: "Party", label: "Party" },
  { value: "Food", label: "Food" },
  { value: "Other", label: "Other" },
];

const validationSchema = yup.object({
  reimbursementType: yup.string().required("Reimbursement type is required"),
  status: yup.string().required("Status is required"),
  description: yup.string().required("Description is required")
    .max(200, "Description cannot be more than 200 characters"),
  comment: yup.string().notRequired()
    .max(200, "Comment cannot be more than 200 characters"),
  submitAmount: yup
    .number()
    .typeError("Amount is required")
    .required("Amount is required")
    .positive("Amount must be positive"),
  spentDate: yup.string().required("Spent Date is required"),
  paidAmount: yup
    .number()
    .notRequired()
    .typeError("Value must be a number")
    .positive("Value must be greater than zero")
    .test(
      "is-valid-city",
      "Approved Amount should not exceed Submitted Amount ",
      async function (inputVal) {
        if (!inputVal) return true;
        const submittedAmount = localStorage.getItem("submittedAmount");
        if (submittedAmount !== null) {
          const sAmount = JSON.parse(submittedAmount as string);
          if (sAmount && inputVal > sAmount) return false;
        }
        return true;
      }
    ),
});

interface IProps {
  onClose: () => void;
  selectedRow: ReimbursementResponse | undefined;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export const status = [
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
];

// ─── Small reusable SVGs ──────────────────────────────────────────────────────

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 3v6h6" />
    <text x="6.5" y="18" fontSize="4.5" fontWeight="bold" fill="currentColor" stroke="none">PDF</text>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const UpdateReimbursement = ({ onClose, selectedRow, onSuccess, onError }: IProps) => {
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [comment, setComment] = useState("");
  const [approvedAmount, setApprovedAmout] = useState(0);
  const [differenceAmount, setDifferenceAmount] = useState(0);
  const [, setSubmitAmount] = useState(0);
  const [spentDate, setSpentDate] = useState("");
  const [description, setDescription] = useState("");

  // Dynamic type options loaded from AdminConfig API
  const [typeOptions, setTypeOptions] = useState<{ value: string; label: string }[]>(FALLBACK_TYPES);

  // File upload state (multi-file)
  const [storageType, setStorageType] = useState<string>("DRIVE");
  // Existing attachments (with ids) the user is keeping — drives `fileIds`.
  const [existingFiles, setExistingFiles] = useState<ReimbursementFile[]>([]);
  // Newly added files (uploaded to S3 eagerly, or sent as DRIVE parts on submit).
  const [newFiles, setNewFiles] = useState<NewAttachment[]>([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const anyUploading = newFiles.some((f) => f.uploading);
  const totalFiles = existingFiles.length + newFiles.length;
  // True when the user added new files or removed any existing attachment.
  const hasFileChanges =
    newFiles.length > 0 ||
    existingFiles.length !== buildExistingFiles(selectedRow).length;

  const { user } = useAppContext();

  const isNonAdminEditable = user.role !== 2 && selectedRow?.status === "INITIATED";
  const isAdminEditable = user.role === 2;
  const canEdit = isAdminEditable || isNonAdminEditable;
  const canEditOwnFile = isNonAdminEditable || (user.role === 2 && user.displayName === selectedRow?.username);

  // ── Fetch dynamic reimbursement types on mount ──────────────────────────────
  useEffect(() => {
    adminConfigService.getAll("api/reimbursement-types")
      .then(({ data }) => {
        if (data.length > 0) {
          setTypeOptions(data.map((item) => ({ value: item.name, label: item.name })));
        }
      })
      .catch(() => { /* keep FALLBACK_TYPES */ });
  }, []);

  // Fetch storage type on mount
  useEffect(() => {
    reimbursementManager.getStorageType()
      .then(({ data }) => setStorageType(data.storage))
      .catch(() => setStorageType("DRIVE"));
  }, []);

  // Revoke object URLs of newly added files on unmount to avoid leaks.
  const newFilesRef = useRef<NewAttachment[]>([]);
  newFilesRef.current = newFiles;
  useEffect(() => {
    return () => {
      newFilesRef.current.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    };
  }, []);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reimbursementType: selectedRow?.type || "",
      status: selectedRow?.status || "",
      description: selectedRow?.description || selectedRow?.comment || "",
      comment: selectedRow?.comment || "",
      paidAmount: selectedRow?.paidAmount || selectedRow?.approveAmount || 0,
      submitAmount: selectedRow?.submitAmount || 0,
      spentDate: selectedRow?.spentDate
        ? new Date(selectedRow.spentDate).toISOString().split("T")[0]
        : "",
    },
  });
  const { formState: { isDirty, isSubmitting } } = methods;

  // ── Form field handlers ────────────────────────────────────────────────────

  const onReimbursementChange = (selectedOp: any) => {
    setSelectedType(selectedOp.value);
    methods.setValue("reimbursementType", selectedOp.value, { shouldDirty: true, shouldValidate: true });
  };

  const onStatusChange = (selectedOp: any) => {
    setSelectedStatus(selectedOp.value);
    methods.setValue("status", selectedOp.value, { shouldDirty: true, shouldValidate: true });
  };

  const onCommentChange = (e: any) => {
    setComment(e.target.value);
    methods.setValue("comment", e.target.value, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmitAmountChange = (e: any) => {
    const amount = e.target.value;
    // Keep an empty string (not undefined) when cleared so the input stays
    // controlled and can be fully emptied. Yup still blocks submit: "" casts to
    // NaN → typeError ("Amount is required"), and 0 fails the positive() rule.
    methods.setValue(
      "submitAmount",
      amount === "" ? ("" as any) : Number(amount),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const onSpentDateChange = (e: any) => {
    const date = e.target.value;
    setSpentDate(date);
    methods.setValue("spentDate", date, { shouldDirty: true, shouldValidate: true });
  };

  const onDescriptionChange = (e: any) => {
    const desc = e.target.value;
    setDescription(desc);
    methods.setValue("description", desc, { shouldDirty: true, shouldValidate: true });
  };

  const onApprovedAmountChange = (e: any) => {
    const amount = e.target.value;
    if (amount === "") {
      setApprovedAmout(amount);
      methods.setValue("paidAmount", undefined, { shouldDirty: true, shouldValidate: true });
      setDifferenceAmount(selectedRow?.submitAmount || 0);
      return;
    }
    const numericAmount = Number(amount);
    setApprovedAmout(numericAmount);
    methods.setValue("paidAmount", Number(amount), { shouldDirty: true, shouldValidate: true });
    setDifferenceAmount((selectedRow?.submitAmount ?? 0) - numericAmount);
  };

  // ── File handlers ─────────────────────────────────────────────────────────

  // Eager S3 upload for a single new attachment; updates its slot by id.
  const uploadNewFileToS3 = (attachment: NewAttachment) => {
    reimbursementManager.getUploadUrl(attachment.file.name)
      .then(async ({ data }) => {
        const putRes = await fetch(data.uploadUrl, { method: "PUT", body: attachment.file });
        if (putRes.status !== 200) throw new Error("S3 upload failed");
        setNewFiles((prev) => prev.map((f) =>
          f.id === attachment.id ? { ...f, s3Key: data.key, uploading: false, uploadError: "" } : f));
      })
      .catch(() => {
        setNewFiles((prev) => prev.map((f) =>
          f.id === attachment.id ? { ...f, uploading: false, uploadError: "Upload failed, please re-add" } : f));
      });
  };

  // Add one or more files: validates size/type, drops duplicates, enforces the
  // per-reimbursement cap, then (S3 mode) kicks off eager uploads.
  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (!picked || !picked.length) return;

    const remaining = MAX_FILES - (existingFiles.length + newFiles.length);
    if (remaining <= 0) {
      setFileError(`You can attach up to ${MAX_FILES} files`);
      e.target.value = "";
      return;
    }

    const accepted: NewAttachment[] = [];
    let lastError = "";

    for (const file of Array.from(picked)) {
      if (accepted.length >= remaining) break;
      if (file.size > MAX_FILE_SIZE) { lastError = `${file.name}: Max file size is 2 MB`; continue; }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) { lastError = INVALID_FILE_TYPE_MESSAGE; continue; }
      // Skip duplicates (same name + size) against already-added new files.
      if (newFiles.some((f) => f.file.name === file.name && f.file.size === file.size)) continue;
      accepted.push({
        id: uid(),
        file,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        s3Key: null,
        uploading: storageType === "S3",
        uploadError: "",
      });
    }

    if (accepted.length) {
      setNewFiles((prev) => [...prev, ...accepted]);
      setFileError("");
      if (storageType === "S3") accepted.forEach(uploadNewFileToS3);
    }
    if (lastError) setFileError(lastError);

    e.target.value = "";
  };

  // Remove a newly added (not-yet-saved) file.
  const removeNewFile = (id: string) => {
    setNewFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  // Drop an existing attachment from the keep-list (won't be in `fileIds`).
  const removeExistingFile = (id: number) => {
    setExistingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = (data: any) => {
    // Guard: an editable user must keep at least one attachment as evidence.
    if (canEditOwnFile && totalFiles === 0) {
      setFileError("At least one attachment is required");
      return;
    }

    let payloadData: any;

    if (user.role === 2 && user.displayName !== selectedRow?.username) {
      const differenceAmount1 = (selectedRow?.submitAmount ?? 0) - (data.paidAmount || 0);
      payloadData = {
        id: selectedRow?.id || 0,
        approveAmount: data.paidAmount || selectedRow?.approveAmount || approvedAmount || 0,
        comment: data.comment || comment || selectedRow?.comment || "",
        description: selectedRow?.description || "",
        differenceAmount: differenceAmount1 || selectedRow?.differenceAmount || 0,
        lastModifiedDate: new Date().toISOString(),
        spentDate: selectedRow?.spentDate || "",
        status: data.status || selectedRow?.status,
        type: data.reimbursementType || selectedRow?.type,
        unit: selectedRow?.unit || "",
        data: null,
      };
    } else {
      payloadData = {
        id: selectedRow?.id || 0,
        type: data.reimbursementType || selectedType || selectedRow?.type,
        description: data.description || description || selectedRow?.description || selectedRow?.comment || "",
        spentDate: data.spentDate || spentDate || selectedRow?.spentDate || "",
        submitAmount: data.submitAmount ?? selectedRow?.submitAmount ?? 0,
        unit: selectedRow?.unit || "",
        lastModifiedDate: new Date().toISOString(),
        data: null,
      };
    }

    const formdata = new FormData();
    formdata.append("reimbursement", new Blob([JSON.stringify(payloadData)], { type: "application/json" }));

    // Only touch attachments when the user actually changed them. When the files
    // are untouched, we send just the payload (no newFiles parts, no query params)
    // so the backend keeps the existing attachments as they were.
    let fileParams: { deleteFileIds?: number[]; newKeys?: string[] } | undefined;
    if (hasFileChanges) {
      // Removed attachments = original files that are no longer kept. Legacy
      // records use synthetic negative ids (no real id) — exclude those.
      const keptIds = new Set(existingFiles.map((f) => f.id));
      const deleteFileIds = buildExistingFiles(selectedRow)
        .map((f) => f.id)
        .filter((fileId) => fileId > 0 && !keptIds.has(fileId));

      // New attachments: DRIVE → multipart `newFiles` parts; S3 → uploaded keys.
      const newKeys: string[] = [];
      if (storageType === "S3") {
        newFiles.forEach((f) => { if (f.s3Key) newKeys.push(f.s3Key); });
      } else {
        newFiles.forEach((f) => formdata.append("newFiles", f.file));
      }

      fileParams = { deleteFileIds, newKeys };
    }

    reimbursementManager.updateReimbursement(formdata, fileParams)
      .then((res) => {
        if (res.status === 200) onSuccess("Reimbursement is Updated successfully");
        else onError("Something went wrong");
      })
      .catch((err) => {
        console.log(`error updating ==> ${err}`);
        onError(err.response.data || "Something went wrong");
      })
      .finally(() => onClose());
  };

  // ── Sync selectedRow → form state ────────────────────────────────────────

  useEffect(() => {
    if (!selectedRow) return;

    setSelectedType(selectedRow.type);

    // Guarantee the stored type is always an option — handles cases where the
    // type was renamed or removed from AdminConfig after the reimbursement was saved.
    if (selectedRow.type) {
      setTypeOptions((prev) =>
        prev.find((o) => o.value === selectedRow.type)
          ? prev
          : [{ value: selectedRow.type, label: selectedRow.type }, ...prev]
      );
    }
    setSelectedStatus(selectedRow.status);
    setComment(selectedRow.comment);

    const initApproved = selectedRow?.paidAmount || selectedRow.approveAmount || 0;
    setApprovedAmout(initApproved);
    setDifferenceAmount(selectedRow.differenceAmount);
    setSubmitAmount(selectedRow.submitAmount || 0);
    setSpentDate(selectedRow.spentDate ? new Date(selectedRow.spentDate).toISOString().split("T")[0] : "");
    const initDesc = selectedRow.description || selectedRow.comment || "";
    setDescription(initDesc);
    localStorage.setItem("submittedAmount", selectedRow.submitAmount.toString());

    methods.reset({
      reimbursementType: selectedRow.type,
      status: selectedRow.status,
      description: initDesc,
      comment: selectedRow.comment || "",
      paidAmount: initApproved,
      submitAmount: selectedRow.submitAmount || 0,
      spentDate: selectedRow.spentDate ? new Date(selectedRow.spentDate).toISOString().split("T")[0] : "",
    });

    // Reset attachment state: keep all current files, clear any staged new ones.
    setNewFiles((prev) => {
      prev.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      return [];
    });
    setExistingFiles(buildExistingFiles(selectedRow));
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [selectedRow, methods]); // eslint-disable-line

  // Shared select classNames
  const selectCls = {
    control: ({ isFocused, isDisabled }: any) =>
      `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? "!ring-2 !ring-blue-500 !border-blue-500" : ""} ${isDisabled ? "!opacity-100" : ""}`,
    placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
    singleValue: () => "!text-gray-900 dark:!text-white !text-sm",
    menu: () => "!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
    option: ({ isFocused, isSelected }: any) =>
      `!text-sm ${isSelected ? "!bg-primary !text-white" : isFocused ? "!bg-primary/10 !text-primary" : "!text-slate-700 dark:!text-slate-300"}`,
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="w-full max-w-5xl mx-auto px-3 bg-white dark:bg-gray-800 rounded-lg">

          {/* ── Warning ──────────────────────────────────────────────────── */}
          {user.role !== 2 && selectedRow?.status !== "INITIATED" && (
            <div className="bg-warning/10 border-l-4 border-warning px-3 py-2 rounded-xl mb-2">
              <div className="flex items-start gap-2">
                <svg className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-warning font-medium">
                  You cannot edit this reimbursement because the status is{" "}
                  <span className="font-bold underline">{selectedRow?.status}</span>.
                </p>
              </div>
            </div>
          )}

          {/* ── Main grid ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5">

            {/* Reimbursement Type */}
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                Reimbursement Type
              </label>
              <Select
                options={typeOptions}
                value={
                  typeOptions.find((op) => op.value === selectedType) ??
                  (selectedType ? { value: selectedType, label: selectedType } : null)
                }
                onChange={onReimbursementChange}
                isDisabled={user.role === 2 && user.displayName !== selectedRow?.username}
                classNames={selectCls}
              />
              <div className="h-4 mt-0.5" />
            </div>

            {/* Submit Amount */}
            <div className="flex flex-col">
              {isNonAdminEditable || (user.role === 2 && user.displayName === selectedRow?.username) ? (
                <Input label="Submit Amount" name="submitAmount" type="number" onChange={onSubmitAmountChange} required />
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">Submit Amount</label>
                  <p className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg p-2.5">
                    {formatCurrency(selectedRow?.submitAmount, selectedRow?.unit)}
                  </p>
                  <div className="h-4 mt-0.5" />
                </>
              )}
            </div>

            {/* Approve Amount + Difference (admin only) */}
            {user.role === 2 && (
              <>
                <div className="flex flex-col">
                  <Input
                    label="Approve Amount" name="paidAmount" type="number"
                    value={approvedAmount} onChange={onApprovedAmountChange}
                    compact disabled={user.displayName === selectedRow?.username}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">Difference Amount</label>
                  <p className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg p-2.5">
                    {formatCurrency(differenceAmount, selectedRow?.unit) || 0}
                  </p>
                  <div className="h-4 mt-0.5" />
                </div>
              </>
            )}

            {/* Spent Date */}
            <div className="flex flex-col">
              {isNonAdminEditable || (user.role === 2 && user.displayName === selectedRow?.username) ? (
                <Input type="date" name="spentDate" label="Spent Date" value={spentDate}
                  onChange={onSpentDateChange} max={new Date().toISOString().split("T")[0]} required />
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">Spent Date</label>
                  <p className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg p-2.5">
                    {selectedRow && dateFormate(selectedRow.spentDate)}
                  </p>
                  <div className="h-4 mt-0.5" />
                </>
              )}
            </div>

            {/* Submit Date */}
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">Submit Date</label>
              <p className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg p-2.5">
                {selectedRow && dateFormate(selectedRow.submitDate)}
              </p>
              <div className="h-4 mt-0.5" />
            </div>

            {/* Status + Unit (admin viewing others) */}
            {user.role === 2 && user.displayName !== selectedRow?.username && (
              <>
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">Status</label>
                  <Select options={status} value={status.find((op) => op.value === selectedStatus)} onChange={onStatusChange} classNames={selectCls} />
                  <div className="h-4 mt-0.5" />
                </div>
                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">Unit</label>
                  <input
                    type="text" disabled value={selectedRow?.unit || "INR"} readOnly
                    className="shared-input-field bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:cursor-not-allowed"
                  />
                  <div className="h-4 mt-0.5" />
                </div>
              </>
            )}

            {/* ── Attachments (full width, multi-file) ─────────────────── */}
            <div className="flex flex-col md:col-span-2 mt-0.5">
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                Attachment{totalFiles > 0 ? ` (${totalFiles})` : ""}
              </label>

              {totalFiles > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* Existing attachments (kept unless removed) */}
                  {existingFiles.map((f) => {
                    const showImage = !isPdfLike(f.originalName) && !isPdfLike(f.url);
                    return (
                      <div key={`ex-${f.id}`} className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
                        <div className="flex-shrink-0 w-12 h-12 rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700">
                          {showImage ? (
                            <img src={getImageUrl(f.url)} alt={f.originalName ?? "attachment"} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <PdfIcon />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate leading-tight">
                            {f.originalName || "Attachment"}
                          </p>
                          <a href={getImageUrl(f.url)} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-500 hover:underline mt-0.5 inline-block">
                            View
                          </a>
                        </div>
                        {canEditOwnFile && (
                          <button type="button" onClick={() => removeExistingFile(f.id)} title="Remove attachment"
                            className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Newly added attachments (not yet saved) */}
                  {newFiles.map((f) => {
                    const showImage = f.file.type.startsWith("image/");
                    return (
                      <div key={`new-${f.id}`} className="flex items-center gap-3 p-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10">
                        <div className="relative flex-shrink-0 w-12 h-12 rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700">
                          {showImage && f.previewUrl ? (
                            <img src={f.previewUrl} alt={f.file.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <PdfIcon />
                            </div>
                          )}
                          <span className="absolute top-0.5 left-0.5 bg-blue-600 text-white text-[8px] font-bold px-1 rounded leading-tight">
                            NEW
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate leading-tight">
                            {f.file.name}
                          </p>
                          {f.uploading ? (
                            <p className="flex items-center gap-1 text-xs text-blue-500 mt-0.5">
                              <SpinnerIcon /> Uploading…
                            </p>
                          ) : f.uploadError ? (
                            <p className="text-xs text-error mt-0.5">{f.uploadError}</p>
                          ) : (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{f.file.type}</p>
                          )}
                        </div>
                        {canEditOwnFile && (
                          <button type="button" onClick={() => removeNewFile(f.id)} title="Remove attachment"
                            className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                !canEditOwnFile && <p className="text-sm text-gray-400 italic">No attachment</p>
              )}

              {/* Add control + help/error (own-editable only) */}
              {canEditOwnFile ? (
                <>
                  {totalFiles < MAX_FILES && (
                    <label className="cursor-pointer self-start mt-2">
                      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {totalFiles > 0 ? "Add More Files" : "Attach Files"}
                      </div>
                      <input ref={fileInputRef} type="file" multiple accept={FILE_ACCEPT}
                        className="sr-only" onChange={handleAddFiles} />
                    </label>
                  )}
                  <div className="h-4 mt-1">
                    {totalFiles === 0
                      ? <p className="text-xs font-medium text-error leading-tight">At least one attachment is required</p>
                      : fileError
                        ? <p className="text-xs font-medium text-error leading-tight">{fileError}</p>
                        : <p className="text-xs text-gray-400 dark:text-gray-500 leading-tight">{`${FILE_HELP_TEXT} · up to ${MAX_FILES} files`}</p>
                    }
                  </div>
                </>
              ) : (
                <div className="h-4 mt-0.5" />
              )}
            </div>
          </div>

          {/* ── Description ──────────────────────────────────────────────── */}
          <div>
            {user.role === 2 && user.displayName !== selectedRow?.username ? (
              <>
                <Input type="textarea" name="comment" label="Reimbursement Description" value={comment} onChange={onCommentChange} />
                {methods.formState.errors.comment && (
                  <span className="text-red-500 text-xs font-medium leading-tight mt-0.5 block">
                    {methods.formState.errors.comment.message as string}
                  </span>
                )}
              </>
            ) : (
              <>
                <Input type="textarea" name="description" label="Reimbursement Description" value={description} onChange={onDescriptionChange} required />
                {methods.formState.errors.description && (
                  <span className="text-red-500 text-xs font-medium leading-tight mt-0.5 block">
                    {methods.formState.errors.description.message as string}
                  </span>
                )}
              </>
            )}
          </div>

          {/* ── Buttons ───────────────────────────────────────────────────── */}
          <div className="flex justify-end gap-2 mt-2">
            <Button color="slate" className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canEdit || (!isDirty && !hasFileChanges) || isSubmitting || anyUploading || (canEditOwnFile && totalFiles === 0)}
              className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
            >
              Update
            </Button>
          </div>

        </div>
      </form>
    </FormProvider>
  );
};

export default UpdateReimbursement;
